import { useState, useRef, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { useCMS } from '../../contexts/CMSContext';
import type { CMSSection } from '../../types/cms';
import { getEffectiveField } from '../../utils/cmsMobile';

type TagType = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';

interface EditableTextProps {
  section: CMSSection;
  field: string;
  tag?: TagType;
  className?: string;
  multiline?: boolean;
  placeholder?: string;
}

export default function EditableText({
  section,
  field,
  tag: Tag = 'p',
  className = '',
  multiline = false,
  placeholder = 'Escribe aquí...',
}: EditableTextProps) {
  const { data, isEditMode, isMobileContentActive, updateField } = useCMS();
  const [isEditing, setIsEditing] = useState(false);
  const value = getEffectiveField(data, section, field, isMobileContentActive);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  useEffect(() => {
    setDraft(value);
  }, [value, data, section, field, isMobileContentActive]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const handleSave = () => {
    updateField(section, field, draft);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraft(value);
    setIsEditing(false);
  };

  const handleBlur = () => {
    if (draft !== value) {
      updateField(section, field, draft);
    }
    setIsEditing(false);
  };

  useEffect(() => {
    if (!isEditMode && isEditing) {
      if (draft !== value) {
        updateField(section, field, draft);
      }
      setIsEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') handleCancel();
  };

  if (!isEditMode) {
    return <Tag className={className}>{value}</Tag>;
  }

  if (isEditing) {
    return (
      <div className="relative inline-block w-full">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={placeholder}
            rows={3}
            className={`${className} w-full border-2 border-[#8B9D83] rounded-lg px-2 py-1 outline-none bg-white/95 resize-none text-sm`}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={`${className} w-full border-2 border-[#8B9D83] rounded-lg px-2 py-1 outline-none bg-white/95 text-sm`}
          />
        )}
        <div className="flex gap-1 mt-1 justify-end">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCancel}
            className="text-xs px-2 py-0.5 rounded bg-stone-200 text-stone-600 hover:bg-stone-300 cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSave}
            className="text-xs px-2 py-0.5 rounded bg-[#8B9D83] text-white hover:bg-[#6B7D63] cursor-pointer whitespace-nowrap"
          >
            Guardar
          </button>
        </div>
      </div>
    );
  }

  return (
    <Tag
      className={`${className} relative group cursor-pointer outline-2 outline-dashed outline-transparent hover:outline-[#8B9D83] rounded px-1 -mx-1 transition-all`}
      onClick={() => setIsEditing(true)}
      title="Haz clic para editar"
    >
      {value || <span className="text-stone-400 italic">{placeholder}</span>}
      <span className="absolute -top-2 -right-2 bg-[#8B9D83] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow z-10">
        <i className="ri-pencil-line"></i>
      </span>
    </Tag>
  );
}
